import { Fragment, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Dropdown, Label, Menu } from '@/components/ui/daisy';
import {
  CheckIcon,
  PencilSquareIcon,
  PhotoIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { EllipsisVerticalIcon } from '@heroicons/react/16/solid';
import { twMerge } from 'tailwind-merge';
import PedWorkspaceGuard from '@/components/shared/PedWorkspaceGuard';

const SHARED_KEY_PREFIX =
  'courseLineForm.questionsTab.createQuestionModal.questionForm';

type AnswerMoreOptionsProps = {
  children: ReactNode;
};

const AnswerMoreOptionsRoot = ({ children }: AnswerMoreOptionsProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'common',
  });

  return (
    <Dropdown position="bottom" align="end">
      <Dropdown.Trigger>
        <Button
          variant="ghost"
          size="xs"
          type="button"
          shape="circle"
          aria-label={t('moreOptions')}
        >
          <EllipsisVerticalIcon className="size-4.5" />
        </Button>
      </Dropdown.Trigger>
      <Dropdown.Content>
        <Menu
          className="bg-base-100 shadow-lg rounded-box w-40"
          activeColor="neutral"
        >
          {children}
        </Menu>
      </Dropdown.Content>
    </Dropdown>
  );
};

type MenuActionProps = {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  className?: string;
};

const MenuAction = ({
  icon,
  label,
  onClick,
  disabled = false,
  className,
}: MenuActionProps) => (
  <PedWorkspaceGuard isBlocked={disabled} placement="right">
    <Menu.Item disabled={disabled}>
      <button
        type="button"
        className={twMerge('flex items-center gap-2', className)}
        onClick={onClick}
        disabled={disabled}
      >
        {icon}
        {label}
      </button>
    </Menu.Item>
  </PedWorkspaceGuard>
);

type ActionProps = {
  onClick: () => void;
  disabled?: boolean;
};

const EditTextAction = (props: ActionProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: SHARED_KEY_PREFIX,
  });

  return (
    <MenuAction
      icon={<PencilSquareIcon className="w-4 h-4" />}
      label={t('editText')}
      className="text-base-content"
      {...props}
    />
  );
};

const EditMediaAction = (props: ActionProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: SHARED_KEY_PREFIX,
  });

  return (
    <MenuAction
      icon={<PhotoIcon className="w-4 h-4" />}
      label={t('editMedia')}
      className="text-base-content"
      {...props}
    />
  );
};

const MarkCorrectAction = (props: ActionProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: SHARED_KEY_PREFIX,
  });

  return (
    <MenuAction
      icon={<CheckIcon className="w-4 h-4 shrink-0" />}
      label={t('markCorrect')}
      className="text-success"
      {...props}
    />
  );
};

const MarkIncorrectAction = (props: ActionProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: SHARED_KEY_PREFIX,
  });

  return (
    <MenuAction
      icon={<XMarkIcon className="w-4 h-4 shrink-0" />}
      label={t('markIncorrect')}
      className="text-error"
      {...props}
    />
  );
};

type ToggleCorrectActionProps = ActionProps & {
  isCorrect: boolean;
};

const ToggleCorrectAction = ({
  isCorrect,
  ...props
}: ToggleCorrectActionProps) => {
  if (isCorrect) return <MarkIncorrectAction {...props} />;
  return <MarkCorrectAction {...props} />;
};

const DeleteAction = (props: ActionProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: SHARED_KEY_PREFIX,
  });

  return (
    <MenuAction
      icon={<TrashIcon className="size-4" />}
      label={t('delete')}
      className="text-error"
      {...props}
    />
  );
};

type PositionsActionProps = {
  totalPositions: number;
  selectedPositions: number[];
  onChange: (positions: number[]) => void;
  header: string;
  getPositionLabel: (position: number) => string;
};

const PositionsAction = ({
  totalPositions,
  selectedPositions,
  onChange,
  header,
  getPositionLabel,
}: PositionsActionProps) => {
  const handleToggle = (position: number) => {
    const next = selectedPositions.includes(position)
      ? selectedPositions.filter(p => p !== position)
      : [...selectedPositions, position];
    const uniquePositions = Array.from(new Set(next));
    const orderedPositions = uniquePositions.sort((a, b) => a - b);
    onChange(orderedPositions);
  };

  return (
    <Fragment>
      <div className="flex flex-col gap-2 px-2 py-1">
        <span className="text-xs font-semibold text-base-content">
          {header}
        </span>
        <Checkbox.Group className="flex flex-col gap-1">
          {Array.from({ length: totalPositions }, (_, i) => i + 1).map(
            position => (
              <Label key={position} className="text-base-content/70">
                <Checkbox
                  size="sm"
                  checked={selectedPositions.includes(position)}
                  onChange={() => handleToggle(position)}
                />
                {getPositionLabel(position)}
              </Label>
            ),
          )}
        </Checkbox.Group>
      </div>
    </Fragment>
  );
};

export const AnswerMoreOptions = Object.assign(AnswerMoreOptionsRoot, {
  EditText: EditTextAction,
  EditMedia: EditMediaAction,
  MarkCorrect: MarkCorrectAction,
  MarkIncorrect: MarkIncorrectAction,
  ToggleCorrect: ToggleCorrectAction,
  Positions: PositionsAction,
  Delete: DeleteAction,
});
